import { CopilotResourceKindEnum } from "~/utils/enums/copilot/chat.enums";
import { VISIOCONFERENCE_LOCATION } from "~/utils/enums/session-items.enums";
import type {
  ICopilotResourceCard,
  ICopilotResourceRow,
  ICopilotResourceRowDeps,
} from "~/utils/interfaces/copilot-interfaces";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Resolves a streamed resource without inferring from the current role, tool, or identifier.
 * Unknown and malformed future payloads are ignored until their kind is explicitly supported.
 */
export function copilotResourceHref(resource: unknown): string | null {
  if (!isRecord(resource) || typeof resource.id !== "string" || !resource.id.trim()) return null;

  const id = encodeURIComponent(resource.id.trim());

  switch (resource.kind) {
    case CopilotResourceKindEnum.COLLABORATOR_PROFILE:
      return `/users?user_id=${id}`;
    case CopilotResourceKindEnum.TRAINING_PLAN_SESSION:
      return `/sessions?id=${id}&type=session`;
    case CopilotResourceKindEnum.EMPLOYEE_TRAINING:
      return `/my-trainings?id=${id}&booking=true`;
    case CopilotResourceKindEnum.EMPLOYEE_REQUEST:
      return `/my-trainings?id=${id}&booking=false`;
    case CopilotResourceKindEnum.COMPANY_REGISTRATION:
      // `GET /trainingPlan/:id` dispatches on the prefix: a bare booking id takes the project branch and 404s.
      return `/registrations?details_id=tr-${id}&type=session`;
    case CopilotResourceKindEnum.COMPANY_REQUEST:
      return `/requests/${id}`;
    case CopilotResourceKindEnum.CATALOG_TRAINING:
      return `/company-catalog?id=${id}`;
    default:
      return null;
  }
}

/** Keyed on the card interface so a mistyped field name fails the typecheck instead of reading blank. */
function optionalStringOf(resource: Record<string, unknown>, key: keyof ICopilotResourceCard): string | undefined {
  const value = resource[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/** The exact linkable cards the resources block can paint; shared with loader visibility to prevent blank bubbles. */
export function copilotResourceCards(resources: unknown): ICopilotResourceCard[] {
  if (!isRecord(resources) || !Array.isArray(resources.items)) return [];

  return resources.items.flatMap((resource) => {
    const href = copilotResourceHref(resource);
    if (!isRecord(resource) || !href) return [];

    const title = optionalStringOf(resource, "title");
    if (!title) return [];

    return [
      {
        href,
        kind: resource.kind as CopilotResourceKindEnum,
        title,
        attendeeName: optionalStringOf(resource, "attendeeName"),
        designation: optionalStringOf(resource, "designation"),
        providerName: optionalStringOf(resource, "providerName"),
        startDate: optionalStringOf(resource, "startDate"),
        endDate: optionalStringOf(resource, "endDate"),
        location: optionalStringOf(resource, "location"),
        status: optionalStringOf(resource, "status"),
        priority: optionalStringOf(resource, "priority"),
        createdAt: optionalStringOf(resource, "createdAt"),
      },
    ];
  });
}

/** The leading glyph carries the kind, so the card needs no "Session :" prefix. */
const KIND_ICONS: Record<CopilotResourceKindEnum, string> = {
  [CopilotResourceKindEnum.COLLABORATOR_PROFILE]: "indiv",
  [CopilotResourceKindEnum.TRAINING_PLAN_SESSION]: "graduate-cap-menu",
  [CopilotResourceKindEnum.EMPLOYEE_TRAINING]: "graduate-cap-menu",
  [CopilotResourceKindEnum.CATALOG_TRAINING]: "graduate-cap-menu",
  [CopilotResourceKindEnum.EMPLOYEE_REQUEST]: "request-menu",
  [CopilotResourceKindEnum.COMPANY_REQUEST]: "request-menu",
  [CopilotResourceKindEnum.COMPANY_REGISTRATION]: "indiv",
};

const DEFAULT_KIND_ICON = "graduate-cap-menu";

function resourceLabelOf(kind: CopilotResourceKindEnum, t: ICopilotResourceRowDeps["t"]): string {
  switch (kind) {
    case CopilotResourceKindEnum.COLLABORATOR_PROFILE:
      return t("ai_companion.resources.profile");
    case CopilotResourceKindEnum.TRAINING_PLAN_SESSION:
      return t("ai_companion.resources.session");
    case CopilotResourceKindEnum.EMPLOYEE_REQUEST:
    case CopilotResourceKindEnum.COMPANY_REQUEST:
      return t("ai_companion.resources.request");
    default:
      return t("ai_companion.resources.training");
  }
}

/** An ISO date is formatted; anything else the backend sends is already display-ready. */
function formattedDateOf(value: string | null | undefined, deps: ICopilotResourceRowDeps): string | null {
  if (!value) return null;

  return /^\d{4}-\d{2}-\d{2}/.test(value) ? deps.formatDate(value) : value;
}

function datesOf(resource: ICopilotResourceCard, deps: ICopilotResourceRowDeps): string | null {
  const start = formattedDateOf(resource.startDate, deps);
  const end = formattedDateOf(resource.endDate, deps);

  if (start && end) return deps.t("ai_companion.resources.date_range", { start, end });
  if (start) return deps.t("ai_companion.resources.date_from", { start });
  if (end) return deps.t("ai_companion.resources.date_until", { end });

  return null;
}

/**
 * Everything the resources block paints, in stream order. Derived here rather than in the block so
 * the row shape is checkable without the Nuxt runtime its labels, formats and status tags need.
 */
export function copilotResourceRows(resources: unknown, deps: ICopilotResourceRowDeps): ICopilotResourceRow[] {
  return copilotResourceCards(resources).map((resource) => ({
    ...resource,
    icon: KIND_ICONS[resource.kind] ?? DEFAULT_KIND_ICON,
    resourceLabel: resourceLabelOf(resource.kind, deps.t),
    dates: datesOf(resource, deps),
    locationIcon: resource.location === VISIOCONFERENCE_LOCATION ? "camera" : "pin",
    statusTag: deps.statusTagOf(resource),
  }));
}
